import { useCallback, useMemo, useState } from 'react';

/**
 * usePageSelection — seleção em massa da PÁGINA VISÍVEL de uma listagem.
 *
 * O bloco existe copiado em `DocumentsContent` (selectedIds/toggleSelect/
 * toggleSelectAllOnPage/clearSelection), no gêmeo `ProductsContent` e ainda em
 * `DatabasesPage`. Imóveis seria a quarta cópia — a regra da casa é promover a
 * primitivo antes disso.
 *
 * A seleção alcança só o que está na tela: nada de "selecionar todos os N do
 * filtro".
 *
 * `resetKey` é o conserto do defeito que existe hoje — a seleção sobrevive a
 * paginar, filtrar, buscar e trocar de visão, e dá para marcar 2 na Lista, ir
 * para Cards e agir em massa sobre linhas que ninguém está vendo. Componha a
 * chave com TUDO que troca o conteúdo da página, inclusive o modo de visão.
 *
 * @example
 * const selection = usePageSelection({
 *   pageIds: items.map((item) => item.id),
 *   resetKey: [databaseId, currentPage, viewMode, searchTerm, statusFilter ?? ''].join('|'),
 * });
 *
 * <SelectAllHeadCell
 *   checked={selection.allOnPageSelected}
 *   indeterminate={selection.someOnPageSelected && !selection.allOnPageSelected}
 *   onToggle={selection.toggleAllOnPage}
 * />
 */

export interface UsePageSelectionOptions {
  /** Ids das linhas visíveis AGORA — base do "marcar todos" e dos derivados. */
  pageIds: string[];
  /** Chave de reinício: mudou, a seleção é zerada. */
  resetKey: string;
}

export interface UsePageSelectionResult {
  /** Ids marcados, em array — é o que vai no corpo da requisição de lote. */
  selectedIds: string[];
  /** Quantos estão marcados (a barra de seleção mostra este número). */
  selectedCount: number;
  /** Se um id específico está marcado (a linha usa para pintar o fundo). */
  isSelected: (id: string) => boolean;
  /** Todos os ids da página estão marcados — `false` com a página vazia. */
  allOnPageSelected: boolean;
  /** Pelo menos um id da página está marcado. */
  someOnPageSelected: boolean;
  /** Marca/desmarca um id. */
  toggle: (id: string) => void;
  /** Marca todos os ids da página, ou desmarca todos se já estavam todos. */
  toggleAllOnPage: () => void;
  /** Desmarca um id — usado depois de excluir aquela linha. */
  deselect: (id: string) => void;
  /** Zera a seleção (botão "Cancelar" da barra e fim de uma ação em massa). */
  clear: () => void;
}

export function usePageSelection({ pageIds, resetKey }: UsePageSelectionOptions): UsePageSelectionResult {
  const [selection, setSelection] = useState<Set<string>>(() => new Set<string>());
  const [appliedResetKey, setAppliedResetKey] = useState<string>(resetKey);

  // Reinício DURANTE o render (padrão do React para estado que depende de uma
  // prop que mudou). Dentro de um useEffect a tela chegaria a pintar uma vez
  // com a seleção velha em cima das linhas novas.
  if (appliedResetKey !== resetKey) {
    setAppliedResetKey(resetKey);
    if (selection.size > 0) setSelection(new Set<string>());
  }

  const toggle = useCallback((id: string) => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const deselect = useCallback((id: string) => {
    setSelection((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    setSelection((prev) => (prev.size === 0 ? prev : new Set<string>()));
  }, []);

  const isSelected = useCallback((id: string) => selection.has(id), [selection]);

  const selectedIds = useMemo(() => Array.from(selection), [selection]);

  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selection.has(id));
  const someOnPageSelected = pageIds.some((id) => selection.has(id));

  // Sem useCallback de propósito: `pageIds` é um array novo a cada render, então
  // a memoização não seguraria identidade nenhuma — só esconderia isso.
  const toggleAllOnPage = () => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };

  return {
    selectedIds,
    selectedCount: selection.size,
    isSelected,
    allOnPageSelected,
    someOnPageSelected,
    toggle,
    toggleAllOnPage,
    deselect,
    clear,
  };
}
